import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./RecordPage.css";
import HomeNavbar from "../HomeNavbar.js";
import Footer from "../Components/Footer.js";
import PageMenu from "../Components/PageMenu.js";
import StatsBox from "../Components/StatsPage/StatsBox.js";
import SmallLoading from "../Components/smallLoading.js";
import ErrorMessage from "../Components/RecordPage/ErrorMessage.js";
import TaxonomyRecord from "../Components/RecordPage/TaxonomyRecord.js";
import MechanismTable from "../mechanismTable.js";
import MechanismWindow from "../Components/MechanismTable/mechanismWindow.js";
import fetchTaxonomyReactions from "../api/taxonomySearch.js";

// The species (`/species/:value`) and kingdom (`/kingdom/:value`) pages. Same
// shell as Client/RecordPage.js — navbar, PageMenu sidebar, footer and the
// shared "View mechanism" modal — with TaxonomyRecord as the body.
//
// `mode` is fixed per route rather than sniffed from the value, because a
// species and a kingdom label are both free text and cannot be told apart the
// way a MARTS id can.
function TaxonomyPage({ mode }) {
  const { value } = useParams();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mechBody, setMechBody] = useState([]);
  const navigate = useNavigate();

  const label = mode === "species" ? "species" : "kingdom";

  const navigateToRecord = (recordId) => {
    navigate(`/record/${recordId}`);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRows(null);

    if (!value) {
      setError({ name: `No ${label} provided`, message: `No ${label} was provided in the URL.` });
      setLoading(false);
      return;
    }

    fetchTaxonomyReactions(mode, value)
      .then(data => {
        if (cancelled) return;
        if (data.length === 0) {
          // Also the "unknown label" case: a name that is not in MARTS returns
          // nothing, and so does a real name whose enzymes have no reactions.
          setError({
            name: `No ${label} found`,
            message: `MARTS holds no enzyme with reactions for the ${label} "${value}".`,
          });
        } else {
          setRows(data);
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError({
          name: "Could not load data",
          message: `Loading the ${label} "${value}" failed. Please try again.`,
        });
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [mode, value, label]);

  useEffect(() => {
    if (value) {
      document.title = `${value} - MARTS-DB`;
    }
    return () => { document.title = "MARTS-DB"; };
  }, [value]);

  return (
    <div id="statsbody">
      <HomeNavbar />
      <PageMenu>
        {loading ? (
          <StatsBox title={"Loading data..."}>
            <SmallLoading />
          </StatsBox>
        ) : error ? (
          <ErrorMessage name={error.name} message={error.message} />
        ) : (
          <TaxonomyRecord
            key={`${mode}:${value}`}
            mode={mode}
            rows={rows}
            setMechBody={setMechBody}
            navigateToRecord={navigateToRecord}
          />
        )}
      </PageMenu>
      <Footer />
      <MechanismWindow>
        <MechanismTable
          heading={["Intermediate substrate", "Intermediate product", "Mechanism type"]}
          body={mechBody}
        />
      </MechanismWindow>
    </div>
  );
}

export default TaxonomyPage;
